
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import RoutineTemplateEditor from '../routines/RoutineTemplateEditor';
import { emptyLiveClassRoutine, routineFromApi } from '../../shared/routineTemplateConstants';
import MastersBackHeader from '../common/MastersBackHeader';
import { emitToast } from '../../context/toast';
import '../d28d/d28d-admin-surfaces.css';

 function buildInitialRoutineForm({ variant = 'platform' } = {}) {
   return emptyLiveClassRoutine({
     scope: variant === 'gym' ? 'gym_wl' : (variant === 'coach' ? 'coach_wl' : 'd28d_platform'),
   });
 }

export default function D28dRoutinesMaster({ onBack, readOnly = false, variant = 'platform', scopeGymId = null }) {
  const isCoach = variant === 'coach';
  const isGym = variant === 'gym';
  const [routines, setRoutines] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(() => buildInitialRoutineForm({ variant }));
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [saveNotice, setSaveNotice] = useState('');
  const [copyingRoutines, setCopyingRoutines] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  const listItemRefs = useRef({});
  useEffect(() => {
    if (!saveNotice) return undefined;
    const t = window.setTimeout(() => setSaveNotice(''), 5000);
    return () => window.clearTimeout(t);
  }, [saveNotice]);

  const loadList = useCallback(async (categoryOverride) => {
    setLoading(true);
    setError('');
    try {
      const activeFilter = categoryOverride !== undefined ? categoryOverride : filterCategoria;
      const listParams = activeFilter ? { categoria: activeFilter } : {};
      if (isCoach) listParams.scope = 'coach';
      if (isGym && scopeGymId) {
        listParams.gym_id = scopeGymId;
        listParams.gym_only = 'true';
      }
      const [listRes, catRes] = await Promise.all([
        api.get('/d28d/routines', { params: listParams }),
        api.get('/d28d/routines/categories', {
          params: isGym && scopeGymId ? { gym_id: scopeGymId, gym_only: 'true' } : {},
        }),
      ]);
      setRoutines(listRes.data?.data || []);
      setCategories(catRes.data?.data || []);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message;
      setError(
        msg
          ? `No se pudo cargar rutinas: ${msg}`
          : 'No se pudo cargar el maestro de rutinas (requiere PostgreSQL).',
      );
    } finally {
      setLoading(false);
    }
  }, [filterCategoria, isCoach, isGym, scopeGymId]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleCopyFromNico = async (force = false) => {
    setCopyingRoutines(true);
    setCopyMessage('');
    setError('');
    try {
      const res = await api.post('/training/admin/routines/copy-from-nico', { force });
      const report = res.data?.report;
      const parts = [];
      if (report?.created) parts.push(`${report.created} nuevas`);
      if (report?.updated) parts.push(`${report.updated} actualizadas`);
      setCopyMessage(parts.length ? `Rutinas copiadas: ${parts.join(', ')}.` : 'Plantilla de Nico aplicada.');
      emitToast({ type: 'success', message: 'Rutinas copiadas desde Nicolás del Río' });
      await loadList();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'No se pudo copiar';
      if (e?.response?.status === 409 && !force) {
        const ok = window.confirm(`${msg}\n\n¿Fusionar con tu catálogo actual?`);
        if (ok) return handleCopyFromNico(true);
      }
      setError(msg);
    } finally {
      setCopyingRoutines(false);
    }
  };

  const loadDetail = async (id) => {
    const res = await api.get(`/d28d/routines/${id}`);
    const data = res.data?.data;
    if (!data) return;
    setSelectedId(data.id);
    setForm(routineFromApi(data));
    if (data.root_id) {
      const hist = await api.get(`/d28d/routines/history/${data.root_id}`);
      setHistory(hist.data?.data || []);
    } else {
      setHistory([]);
    }
  };

  const handleSave = async (newVersion = false) => {
    if (readOnly) return;
    try {
      setSaving(true);
      setError('');
      setSaveNotice('');
      const saveBody = {
        ...form,
        scope: isGym ? 'gym_wl' : (form.scope || (isCoach ? 'coach_wl' : 'd28d_platform')),
        ...(isGym && scopeGymId ? { gym_id: Number(scopeGymId) } : {}),
      };
      if (selectedId) {
        const res = await api.put(`/d28d/routines/${selectedId}`, { ...saveBody, new_version: newVersion });
        const savedId = newVersion ? res.data?.data?.id : selectedId;
        const listFilter = form.categoria?.trim() || '';
        if (listFilter !== filterCategoria) setFilterCategoria(listFilter);
        await loadList(listFilter);
        if (savedId) {
          setSelectedId(savedId);
          await loadDetail(savedId);
          window.requestAnimationFrame(() => {
            listItemRefs.current[savedId]?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
          });
        }
        const label = form.nombre?.trim() || 'Rutina';
        const msg = newVersion ? `Nueva versión de «${label}» guardada.` : `Cambios guardados en «${label}».`;
        setSaveNotice(msg);
        emitToast({ type: 'success', title: newVersion ? 'Nueva versión' : 'Rutina guardada', message: msg });
      } else {
        const res = await api.post('/d28d/routines', saveBody);
        const newId = res.data?.data?.id;
        const label = form.nombre?.trim() || res.data?.data?.nombre || 'Rutina';
        const listFilter = form.categoria?.trim() || '';
        if (listFilter !== filterCategoria) setFilterCategoria(listFilter);
        if (newId) {
          setSelectedId(newId);
          await loadList(listFilter);
          await loadDetail(newId);
          window.requestAnimationFrame(() => {
            listItemRefs.current[newId]?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
          });
        } else {
          await loadList(listFilter);
        }
        const msg = `«${label}» quedó guardada. La ves en la lista de la izquierda.`;
        setSaveNotice(msg);
        emitToast({ type: 'success', title: 'Rutina creada', message: msg });
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Error guardando rutina.');
      emitToast({
        type: 'error',
        title: 'No se pudo guardar',
        message: e.response?.data?.message || 'Error guardando rutina.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    if (!selectedId || readOnly) return;
    const res = await api.post(`/d28d/routines/${selectedId}/duplicate`);
    await loadList();
    await loadDetail(res.data?.data?.id);
  };

  const handleArchive = async () => {
    if (!selectedId || readOnly || !window.confirm('¿Archivar esta rutina?')) return;
    await api.post(`/d28d/routines/${selectedId}/archive`);
    await loadList();
    setSelectedId(null);
    setForm(buildInitialRoutineForm({ variant }));
  };

  const handleImport = async () => {
    if (readOnly || !window.confirm('Importar plantillas D28D del catálogo base? (omite duplicados)')) return;
    const res = await api.post('/d28d/routines/import/bundled');
    const r = res.data?.data;
    alert(`Importación: ${r.created} creadas, ${r.skipped} omitidas.`);
    await loadList();
  };

  const addCategory = async () => {
    if (readOnly || !newCategory.trim()) return;
    const payload = { nombre: newCategory.trim() };
    if (isGym && scopeGymId) payload.gym_id = Number(scopeGymId);
    await api.post('/d28d/routines/categories', payload);
    setNewCategory('');
    await loadList();
  };

  const startEditCategory = (cat) => {
    if (readOnly || cat?.id == null || String(cat.id).startsWith('routine-')) return;
    setEditingCategoryId(cat.id);
    setEditingCategoryName(cat.nombre || '');
  };

  const saveEditCategory = async () => {
    if (readOnly || editingCategoryId == null || !editingCategoryName.trim()) return;
    const payload = { id: Number(editingCategoryId), nombre: editingCategoryName.trim() };
    if (isGym && scopeGymId) payload.gym_id = Number(scopeGymId);
    try {
      await api.put('/d28d/routines/categories', payload);
      setEditingCategoryId(null);
      setEditingCategoryName('');
      if (filterCategoria && form.categoria === filterCategoria) {
        setForm((f) => ({ ...f, categoria: editingCategoryName.trim() }));
      }
      await loadList();
      setSaveNotice(`Categoría actualizada a «${editingCategoryName.trim()}».`);
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo editar la categoría');
    }
  };

  const deleteCategory = async (cat) => {
    if (readOnly || cat?.id == null || String(cat.id).startsWith('routine-')) return;
    const others = categories
      .filter((c) => Number(c.id) !== Number(cat.id) && !String(c.id).startsWith('routine-'))
      .map((c) => c.nombre);
    const reassignHint = others.length
      ? `\n\nLas rutinas con esta categoría se reasignarán a «${others[0]}».`
      : '\n\nSi hay rutinas con esta categoría, quedarán con el nombre anterior hasta que las edites.';
    if (!window.confirm(`¿Eliminar la categoría «${cat.nombre}»?${reassignHint}`)) return;
    try {
      const params = {};
      const body = {};
      if (isGym && scopeGymId) {
        params.gym_id = scopeGymId;
        body.gym_id = Number(scopeGymId);
      }
      if (others[0]) body.reassign_to = others[0];
      await api.delete(`/d28d/routines/categories/${cat.id}`, { params, data: body });
      if (filterCategoria === cat.nombre) setFilterCategoria('');
      if (form.categoria === cat.nombre && others[0]) {
        setForm((f) => ({ ...f, categoria: others[0] }));
      }
      await loadList(filterCategoria === cat.nombre ? '' : undefined);
      setSaveNotice(`Categoría «${cat.nombre}» eliminada.`);
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo eliminar la categoría');
    }
  };

  const categoryOptions = useMemo(() => {
    const names = categories.map((c) => c.nombre);
    if (form.categoria && !names.includes(form.categoria)) names.push(form.categoria);
    return names;
  }, [categories, form.categoria]);

  const editableCategories = useMemo(
    () => categories.filter((c) => c?.id != null && !String(c.id).startsWith('routine-')),
    [categories],
  );

  const sidebarRoutines = useMemo(() => {
    if (!selectedId || routines.some((r) => r.id === selectedId)) return routines;
    return [
      {
        id: selectedId,
        nombre: form.nombre || 'Rutina en edición',
        categoria: form.categoria || '—',
        version: '…',
        duracion: form.duracion || '',
        _filteredOut: true,
      },
      ...routines,
    ];
  }, [routines, selectedId, form.nombre, form.categoria, form.duracion]);

  return (
    <div className="d28d-admin-surface surface-readable dashboard-main-view">
      <header className="dashboard-header panel-admin-header">
        <div>
          {isGym && onBack ? (
            <MastersBackHeader onBack={onBack} title="Rutinas de tu gimnasio" />
          ) : onBack ? (
            <button type="button" className="btn-secondary panel-back-btn" onClick={onBack}>
              {isCoach ? '← Entrenadores' : '← Maestros'}
            </button>
          ) : null}
          {!(isGym && onBack) ? (
            <>
              <h2 className="d28d-page-title">
                {isGym
                  ? 'Rutinas de tu gimnasio'
                  : isCoach
                    ? 'Mis rutinas de entrenamiento'
                    : 'Maestro de Rutinas D28D'}
              </h2>
              <p className="d28d-text-muted">
                {isGym
                  ? 'Plantillas propias de tu sede para clases en vivo. No se mezclan con las rutinas de la plataforma D28D.'
                  : isCoach
                    ? 'Tus plantillas privadas: bloques, ejercicios y videos. No compartes catálogo con D28D.'
                    : readOnly
                      ? 'Vista de consulta. Las observaciones de sesión se registran en clases en vivo.'
                      : 'Plantillas reutilizables (mismo modelo que Training). Versionado sin alterar clases ya programadas.'}
              </p>
            </>
          ) : (
            <p className="text-sm text-stone-600 mt-1">
              Plantillas propias de tu sede para clases en vivo. No se mezclan con las rutinas de la plataforma D28D.
            </p>
          )}
        </div>
        {!readOnly && !isCoach && !isGym && (
          <div className="flex gap-2 flex-wrap">
            <button type="button" className="btn-secondary" onClick={handleImport}>Importar catálogo D28D</button>
            <button type="button" className="btn-primary" onClick={() => { setSelectedId(null); setForm(buildInitialRoutineForm({ variant })); setHistory([]); }}>
              Nueva rutina
            </button>
          </div>
        )}
        {!readOnly && isGym && (
          <div className="flex gap-2 flex-wrap">
            <button type="button" className="btn-primary" onClick={() => { setSelectedId(null); setForm(buildInitialRoutineForm({ variant })); setHistory([]); }}>
              Nueva rutina
            </button>
          </div>
        )}
        {!readOnly && isCoach && (
          <div className="flex gap-2 flex-wrap items-end">
            <button type="button" className="btn-primary" onClick={() => { setSelectedId(null); setForm(buildInitialRoutineForm({ variant })); setHistory([]); }}>
              Nueva rutina
            </button>
            <button
              type="button"
              className="btn-secondary text-sm"
              disabled={copyingRoutines}
              onClick={() => handleCopyFromNico(false)}
            >
              {copyingRoutines ? 'Copiando…' : routines.length === 0 ? 'Copiar rutinas de Nicolás del Río' : 'Copiar más de Nico'}
            </button>
          </div>
        )}
      </header>

      {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">{error}</div>}
      {copyMessage && <div className="bg-green-50 text-green-800 text-sm p-3 rounded-lg mb-4">{copyMessage}</div>}
      {saveNotice && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-4 rounded-lg mb-4 text-sm" role="status">
          {saveNotice}
        </div>
      )}

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <aside className="card p-4 ui-light-panel">
          <h3 className="text-sm font-semibold text-stone-900 mb-1">
            Rutinas guardadas
            {!loading ? ` (${routines.length})` : ''}
          </h3>
          {selectedId && !routines.some((r) => r.id === selectedId) ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
              La rutina que editas no coincide con el filtro «{filterCategoria || 'Todas'}». Cambia categoría arriba o elige «Todas».
            </p>
          ) : null}
          <p className="text-xs text-stone-500 mb-3">
            Aquí revisas las que ya creaste. Haz clic en una para editarla.
          </p>
          <label className="block text-sm font-semibold mb-2">Filtrar categoría</label>
          <select className="input w-full mb-3" value={filterCategoria} onChange={(e) => setFilterCategoria(e.target.value)}>
            <option value="">Todas</option>
            {categoryOptions.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          {!readOnly && (
            <div className="flex gap-2 mb-2">
              <input className="input w-full text-sm" placeholder={isGym ? 'Nueva categoría de tu sede' : 'Nueva categoría'} value={newCategory} onChange={(e) => setNewCategory(e.target.value)} />
              <button type="button" className="btn-secondary text-sm shrink-0" onClick={addCategory}>+</button>
            </div>
          )}
          {!readOnly && editableCategories.length > 0 && (
            <ul className="mb-4 space-y-1 max-h-40 overflow-y-auto text-sm">
              {editableCategories.map((cat) => (
                <li key={cat.id} className="flex items-center gap-1 rounded border border-stone-200 bg-white px-2 py-1">
                  {editingCategoryId === cat.id ? (
                    <>
                      <input
                        className="input w-full text-xs py-1"
                        value={editingCategoryName}
                        onChange={(e) => setEditingCategoryName(e.target.value)}
                      />
                      <button type="button" className="btn-primary text-xs shrink-0 px-2 py-1" onClick={saveEditCategory}>OK</button>
                      <button type="button" className="btn-secondary text-xs shrink-0 px-2 py-1" onClick={() => { setEditingCategoryId(null); setEditingCategoryName(''); }}>✕</button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 truncate text-stone-800">{cat.nombre}</span>
                      <button type="button" className="btn-secondary text-xs shrink-0 px-2 py-1" onClick={() => startEditCategory(cat)}>Editar</button>
                      <button type="button" className="btn-secondary text-xs shrink-0 px-2 py-1 text-red-700" onClick={() => deleteCategory(cat)}>Eliminar</button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
          {loading ? (
            <p className="text-sm text-slate-500">Cargando…</p>
          ) : sidebarRoutines.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-stone-600">
              {filterCategoria
                ? `No hay rutinas en «${filterCategoria}». Elige «Todas» o crea una nueva a la derecha.`
                : 'Aún no tienes rutinas. Completa el formulario de la derecha y pulsa «Crear rutina».'}
            </div>
          ) : (
            <ul className="space-y-2 max-h-[60vh] overflow-y-auto">
              {sidebarRoutines.map((r) => (
                <li key={r.id} ref={(el) => { listItemRefs.current[r.id] = el; }}>
                  <button
                    type="button"
                    className={`w-full text-left rounded-xl border p-3 text-sm transition-colors ${selectedId === r.id ? 'border-lime-500 bg-lime-50 ring-2 ring-lime-200' : 'border-slate-200 hover:border-slate-300'}${r._filteredOut ? ' border-dashed border-amber-300' : ''}`}
                    onClick={() => loadDetail(r.id)}
                  >
                    <div className="font-semibold">{r.nombre}</div>
                    <div className="text-xs text-slate-500">
                      {r.categoria} · v{r.version}
                      {r.duracion ? ` · ${r.duracion}` : ''}
                      {selectedId === r.id ? ' · editando' : ''}
                      {r._filteredOut ? ' · fuera del filtro' : ''}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="card p-5 space-y-4 ui-light-panel">
          {selectedId ? (
            <p className="text-xs text-stone-500 border-b border-slate-200 pb-3">
              Editando rutina guardada · los cambios se aplican con «Guardar cambios»
            </p>
          ) : (
            <p className="text-xs text-stone-500 border-b border-slate-200 pb-3">
              Nueva rutina · al pulsar «Crear rutina» aparecerá en la lista de la izquierda
            </p>
          )}
          <RoutineTemplateEditor
            form={form}
            setForm={setForm}
            categoryOptions={categoryOptions}
            readOnly={readOnly}
            compact={isGym}
            liveClass={!isGym}
          />

          {!readOnly && (
            <div className="flex flex-wrap gap-2 pt-4 border-t">
              <button type="button" className="btn-primary" disabled={saving} onClick={() => handleSave(false)}>
                {selectedId ? 'Guardar cambios' : 'Crear rutina'}
              </button>
              {selectedId && (
                <>
                  <button type="button" className="btn-secondary" disabled={saving} onClick={() => handleSave(true)}>Nueva versión</button>
                  <button type="button" className="btn-secondary" onClick={handleDuplicate}>Duplicar</button>
                  <button type="button" className="btn-danger" onClick={handleArchive}>Archivar</button>
                </>
              )}
            </div>
          )}

          {history.length > 1 && (
            <div className="mt-6">
              <h4 className="font-semibold mb-2">Historial de versiones</h4>
              <ul className="text-sm space-y-1">
                {history.map((h) => (
                  <li key={h.id}>
                    <button type="button" className="text-lime-700 underline" onClick={() => loadDetail(h.id)}>
                      v{h.version} — {h.nombre} {h.is_current ? '(actual)' : ''}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
